import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BellRing, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DISTRICTS = ["Any", "Wakiso", "Mukono", "Luweero", "Jinja", "Mbarara", "Gulu", "Kampala"];

export default function DealAlertOptIn() {
  const [email, setEmail] = useState("");
  const [district, setDistrict] = useState("Any");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setSubmitting(true);
    try {
      await api.post("/deal-alerts", {
        email,
        district: district !== "Any" ? district : null,
      });
      setDone(true);
      toast.success("You're on the list — check your email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-primary/5 border border-primary/30 rounded-3xl p-6 flex items-center gap-4" data-testid="deal-alert-success">
        <div className="w-11 h-11 rounded-full bg-primary/15 grid place-items-center text-primary shrink-0">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-lg font-semibold">You&apos;re subscribed</div>
          <p className="text-sm text-muted-foreground">We&apos;ll email <b>{email}</b> the moment a matching plot lands.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-secondary text-secondary-foreground rounded-3xl p-6 md:p-8 grid md:grid-cols-12 gap-4 items-center relative overflow-hidden" data-testid="deal-alert-form">
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      <div className="md:col-span-4 relative z-10">
        <div className="flex items-center gap-2 mb-1">
          <BellRing className="w-4 h-4 text-accent" />
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-accent">Deal Alerts</div>
        </div>
        <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight">Get pinged when a matching plot lands.</h3>
      </div>
      <div className="md:col-span-4 relative z-10">
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="h-11 rounded-full border-white/25 bg-white/10 text-white placeholder:text-white/60 focus-visible:ring-accent"
          data-testid="deal-alert-email"
        />
      </div>
      <div className="md:col-span-2 relative z-10">
        <Select value={district} onValueChange={setDistrict}>
          <SelectTrigger className="h-11 rounded-full border-white/25 bg-white/10 text-white" data-testid="deal-alert-district">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>{DISTRICTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2 relative z-10">
        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-11 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
          data-testid="deal-alert-submit"
        >
          {submitting ? "Subscribing…" : "Notify me"}
        </Button>
      </div>
    </form>
  );
}
